


































































































import { Component, Vue } from 'vue-property-decorator';
import { ValidationObserver, ValidationProvider } from 'vee-validate-3';

import CustomerIOService from '@/api/CustomerIOService';

import HTTPStatus from '@/constants/httpStatus';

import SelectDropDownFieldUtils from '@/utils/selectDropdownFieldUtils';

import { VueMultiselectOptions } from '@/models/select-dropdown';

import Page from '@/componentsV2/molecules/Page/Page.vue';
import SelectDropdownField from '@/componentsV2/molecules/SelectDropdownField/SelectDropdownField.vue';
import Input from '@/componentsV2/molecules/Inputs/TextField.vue';
import FilledButton from '@/components/molecules/FilledButton/FilledButton.vue';
import Container from '@/components/atoms/Grid/Container.vue';

type CSVRecord = Record<string, string>;

type FormData = {
  workspace: VueMultiselectOptions | null;
  broadcast: VueMultiselectOptions | null;
  perUserData: Record<string, any>[];
};

function defaultOptionValue() {
  return {
    value: '',
    label: ''
  };
}

function defaultFormData() {
  return {
    workspace: null,
    broadcast: null,
    perUserData: []
  };
}

export type RequestData = Record<string, any> | undefined;

@Component({
  components: {
    ValidationObserver,
    ValidationProvider,
    FilledButton,
    Container,
    Page,
    SelectDropdownField,
    Input
  }
})
export default class TriggerBroadCasts extends Vue {
  customerIOService = new CustomerIOService();

  $refs!: {
    observer: InstanceType<typeof ValidationObserver>;
    selectWorkspaceRef: SelectDropdownField;
    selectBroadcastRef: SelectDropdownField;
  };

  loading = false;
  loadingBroadcasts = false;
  formData: FormData = defaultFormData();

  // workspace select
  workspaceDefault = defaultOptionValue();
  workspaceOptions: VueMultiselectOptions[] = [];
  workspaceSelectOptions = {
    ...SelectDropDownFieldUtils.getSelectdropdownPropertiesDefault(),
    searchable: false
  };

  // broadcast select
  broadcastDefault = defaultOptionValue();
  broadcastOptions: VueMultiselectOptions[] = [];
  broadcastSelectOptions = {
    ...SelectDropDownFieldUtils.getSelectdropdownPropertiesDefault(),
    useSearchIcon: true
  };

  created() {
    this.fillWorkspaces();
  }

  getWorkspacesByEnv() {
    switch (process.env.VUE_APP_ENV) {
      case 'production':
        return [
          {
            label: 'Twism Consumers',
            value: 'consumers'
          },
          {
            label: 'Twism Development',
            value: 'development'
          },
          {
            label: 'Twism Merchants',
            value: 'merchants'
          }
        ];
      case 'qa':
      case 'staging':
      case 'development':
      default:
        return [
          {
            label: 'Twism Development',
            value: 'development'
          }
        ];
    }
  }

  fillWorkspaces() {
    const workspaces = this.getWorkspacesByEnv();

    this.workspaceOptions = workspaces;

    if (workspaces.length === 1) {
      const firstWorkspace = workspaces[0];

      this.workspaceDefault = firstWorkspace;
      this.formData.workspace = firstWorkspace;
      this.handleWorkspaceSelect(firstWorkspace);
    }
  }

  handleClearWorkspace() {
    this.formData.workspace = defaultOptionValue();
  }

  handleClearBroascast() {
    this.formData.broadcast = defaultOptionValue();
  }

  async handleCsvUpload(event: Event) {
    const formRef = this.$refs.observer;

    const input = event.target as HTMLInputElement;
    const file = input.files ? input.files[0] : null;

    if (!file) {
      formRef.setErrors({
        broadcastData: [
          String(this.$t('common.validations.fields.broadcastData.required'))
        ]
      });

      return;
    }

    formRef.setErrors({ data: [] });

    const reader = new FileReader();

    reader.onload = (e: ProgressEvent<FileReader>) => {
      if (e.target && e.target.result) {
        const csvData = e.target.result as string;

        const result = this.csvToJson(csvData);

        if (!result) {
          formRef.setErrors({ broadcastData: ['Invalid CSV file'] });

          return;
        }

        if (!result.length) {
          formRef.setErrors({ broadcastData: [`CSV file hasn't lines`] });

          return;
        }

        this.formData.perUserData = result;
      }
    };

    reader.readAsText(file);
  }

  csvToJson(csv: string): Array<CSVRecord> | null {
    const lines = csv.split('\n');

    const headers = lines[0]
      .split(',')
      .map(header => header.trim().replace(/"/g, ''));

    if (!headers.includes('email')) return null;

    return lines.slice(1).map(line => {
      const regex = /(".*?"|[^",\s][^,]*[^",\s]|[^",\s]+)(?=\s*,|\s*$)/g;

      const values = Array.from(line.matchAll(regex), match =>
        match[0]
          .replace(/""/g, '"')
          .replace(/(^"|"$)/g, '')
          .trim()
      );

      return headers.reduce<CSVRecord>(
        (obj, header, index) => ({
          ...obj,
          [header]: values[index]
        }),
        {}
      );
    });
  }

  async handleWorkspaceSelect(selectedOption: VueMultiselectOptions) {
    this.broadcastOptions = [];

    try {
      this.loadingBroadcasts = true;

      const { data } = await this.customerIOService.listBroadcasts(
        selectedOption.value
      );

      this.broadcastOptions = data.broadcasts
        .filter(({ active }) => active)
        .map(broadcast => ({
          value: String(broadcast.id),
          label: broadcast.name
        }));
    } finally {
      this.loadingBroadcasts = false;
    }
  }

  handleError(error: unknown) {
    if (typeof error === 'object' && error !== null && 'errors' in error) {
      const errorObject = error as {
        errors: Array<{
          detail: string;
          source?: { pointer?: string };
          status?: string;
        }>;
      };

      if (Array.isArray(errorObject.errors)) {
        errorObject.errors.forEach(err => {
          const detail = err.detail;

          if (detail) {
            this.$notification(detail, 'error');
          }
        });

        return;
      }
    }

    this.$notification('Failed to send emails', 'error');
  }

  async triggerBroadcast() {
    const { broadcast, workspace, perUserData } = this.formData;

    if (!workspace || !broadcast) return;

    this.loading = true;

    const { status, data } = await this.customerIOService.triggerBroadcast({
      workspace: workspace.value,
      broadcastId: Number(broadcast.value),
      payload: {
        id_ignore_missing: true,
        email_ignore_missing: true,
        per_user_data: perUserData.map(userData => {
          const { email, ...rest } = userData;

          return {
            email,
            data: rest
          };
        })
      }
    });

    this.loading = false;

    if (status !== HTTPStatus.CREATED) {
      this.handleError(data);

      return;
    }

    this.reset();
    this.$notification('Emails sent successfully', 'success');
  }

  reset() {
    const formRef = this.$refs.observer;

    this.$refs.selectWorkspaceRef.triggerClearAll();
    this.$refs.selectBroadcastRef.triggerClearAll();
    this.formData = Object.assign({}, this.formData, defaultFormData());
    this.broadcastOptions = [];

    formRef.reset();

    const csvFileInput = document.getElementById(
      'broadcastData'
    ) as HTMLInputElement;

    if (csvFileInput) {
      csvFileInput.value = '';
    }
  }

  async onSubmit() {
    const formRef = this.$refs.observer;
    const formIsValid = await formRef.validate();

    if (!this.formData.perUserData.length) {
      formRef.setErrors({
        broadcastData: [
          String(this.$t('common.validations.fields.broadcastData.required'))
        ]
      });

      return;
    }

    if (!formIsValid) return;

    this.triggerBroadcast();
  }
}
